import { Box, Tab, Tabs, ThemeProvider, Button } from "@mui/material";
import { isPartialPatternMatch } from "components/modules/policy-editor/utils/stringExtensions";
import ReviewWarningsDialog from "components/modules/policy-editor/components/mainPage/applicabilityRulesTab/ReviewWarningsDialog";
import { MainApplicabilityRulesTab } from "components/modules/policy-editor/components/mainPage";
import ActiveDirectoryDomainTab from "components/modules/policy-editor/components/mainPage/activeDirectoryDomainTab/ActiveDirectoryDomainTab";
import ActiveDirectoryOUTab from "components/modules/policy-editor/components/mainPage/adOusTab/ActiveDirectoryOUTab";
import DiffModal from "components/modules/policy-editor/components/mainPage/applicabilityRulesTab/DiffModal";
import ClientConfigTab from "components/modules/policy-editor/components/mainPage/clientConfig";
import { SitesAndSubnetsTab } from "components/modules/policy-editor/components/mainPage/sitesAndSubnets/SitesAndSubnetsTab";
import SystemConfigTab from "components/modules/policy-editor/components/mainPage/systemConfigTab/SystemConfigTab";
import { ConfirmationDialog, ErrorMessage } from "components/ui";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useApi, useConfiguration, usePolicyEditorPermissions } from "system/hooks";
import usePageTitle from "system/hooks/use-page-title";
import { RootState } from "system/store";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { muiTheme } from "system/styles/muiTheme";
import { IniInfo } from "system/types/interfaces";
import { CompanyPolicyStatus } from "components/modules/policy-editor/components/mainPage/persistenceTab/types";
import { processFinalRules, splitConfigurations } from "system/utils";
import { RuleRow } from "components/modules/policy-editor/utils";
import { validateRule } from "components/modules/policy-editor/utils/ruleValidator";
import * as parseRules from "components/modules/policy-editor/utils/parseRules";
import { getConfigRuleError, getConfigValueError, isCommentRow, normalizeConfigValue } from "components/modules/policy-editor/utils/configValueQuoting";
import PendingChangesSummaryModal from "components/modules/policy-editor/components/mainPage/applicabilityRulesTab/PendingChangesSummaryModal";
import { tagOriginal } from "components/modules/policy-editor/utils/ruleSignature";

export const PolicyEditorPage = () => {
  const {
    licenseFileValid,
    invalidConfigs,
    invalidRules,
    sitesAndSubnets,
    systemConfigurations,
    activeDirectoryDomains,
    clientConfigModified,
    clientConfig,
    inheritedConfigurations,
    configurationByPackage,
    rules,
    activeDirectoryOUs,
    modifiedRows,
    pendingReplicateChanges,
    initialPendingReplicateChanges,
    packages,
    pendingConfigSections,
  } = useSelector((state: RootState) => state.policyEditor);

  const packagesMissingCount = useMemo(() => {
    if (packages.length === 0) return 0;
    return rules.filter((rule) => {
      if (rule.isDeleted || rule.isComment || rule.name?.includes("-comment-") || !rule.id || !rule.name) return false;
      const ruleName = rule.name.trim();
      if (!ruleName) return false;
      const inReplicate = packages.some((p) => p.value && isPartialPatternMatch(p.value, ruleName));
      const inPending = pendingReplicateChanges.additions.some((a) => isPartialPatternMatch(a, ruleName));
      const willBeRemoved = pendingReplicateChanges.removals.some((r) => isPartialPatternMatch(r, ruleName));
      if ((inReplicate || inPending) && !willBeRemoved) return false;
      if (willBeRemoved && inPending) return false;
      const hasSyntaxError = invalidRules.some((ir) => ir.rule.id === rule.id);
      if (hasSyntaxError) return false;
      return true;
    }).length;
  }, [rules, packages, pendingReplicateChanges, invalidRules]);

  const { versionId } = useParams<{ versionId: string }>();
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState(0);
  const { currentTenant } = useConfiguration();
  const { isReadOnly: readOnlyViewer } = usePolicyEditorPermissions();
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [versionNumber, setVersionNumber] = useState<number | null>(null);

  const [diffModalOpen, setDiffModalOpen] = useState(false);
  const [originalIni, setOriginalIni] = useState("");
  const [newIni, setNewIni] = useState("Loading...");
  const [generatedText, setGeneratedText] = useState("");
  const [replicateOriginal, setReplicateOriginal] = useState("");
  const [replicateGenerated, setReplicateGenerated] = useState("");
  const [reviewWarningsOpen, setReviewWarningsOpen] = useState(false);
  const [pendingSummaryOpen, setPendingSummaryOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState<{ title: string; message: string; visible: boolean }>({
    title: "",
    message: "",
    visible: false,
  });

  const dispatch = useDispatch();
  const api = useApi();

  useEffect(() => {
    if (packages.length === 0 || !pendingConfigSections) return;
    const { normal, inherited } = splitConfigurations(pendingConfigSections, packages);
    const normalWithIds = normal.map((section) => ({
      ...section,
      lines: section.lines.map((line, index) => tagOriginal({ ...line, id: line.id ?? crypto.randomUUID() }, index)),
    }));
    const inheritedWithIds = inherited.map((config) => ({
      ...config,
      configurations: config.configurations.map((c) => ({ ...c, id: c.id ?? crypto.randomUUID() })),
    }));
    dispatch(policyEditorActions.setConfigurationForPackage(normalWithIds));
    dispatch(policyEditorActions.setInheritedConfigurations(inheritedWithIds));
    dispatch(policyEditorActions.setPendingConfigSections(null));
  }, [packages, pendingConfigSections, dispatch]);

  useEffect(() => {
    if (systemConfigurations.length === 0) return;
    const validationResults = systemConfigurations.map((config, index) => {
      if (config.name.includes("-comment-") || config.isComment) return { index, rule: config, isValid: true, errors: [] };

      const combined = `${config.delimiter} ${config.value}`.trim();
      const finalResult = parseRules.getSegmentedRule(combined);
      const rows: RuleRow[] = finalResult.map((seg) => ({
        delimiter: seg.delimiter,
        alias: seg.alias,
        property: seg.property,
        operator: seg.operator,
        value: seg.value,
      }));
      const result = validateRule(rows);
      const nameErrors: string[] = [];
      if (!config.name.match(/^[a-zA-Z0-9][a-zA-Z0-9_-]*[a-zA-Z0-9]$/)) {
        nameErrors.push("Invalid name format.");
      }
      return {
        index: index + 1,
        rule: config,
        isValid: result.isValid && nameErrors.length === 0,
        errors: [...result.errors, ...nameErrors],
      };
    });

    const invalidConfigs = validationResults.filter((result) => !result.isValid);
    dispatch(policyEditorActions.setInvalidConfigs(invalidConfigs));
  }, [systemConfigurations, dispatch]);

  // Client Config, package configurations and inherited configurations were only ever validated
  // inside the edit dialog, so a rule left invalid there reached the policy with nothing to say
  // so. They are checked here with the same applicability validation the rule builder uses. Kept
  // apart from invalidConfigs, which the System Configurations tab reads as its own.
  const invalidConfigRules = useMemo(() => {
    const check = (source: string, rule: IniInfo) => {
      if (rule.isDeleted || isCommentRow(rule.name, rule.value, rule.isComment)) return null;
      const error =
        getConfigRuleError(rule.delimiter ?? "", rule.applicability ?? "") ??
        getConfigValueError(rule.name ?? "", rule.value ?? "", rule.delimiter ?? "", rule.applicability ?? "");
      return error ? { source, rule, error } : null;
    };

    const found: { source: string; rule: IniInfo; error: string }[] = [];
    clientConfig.forEach((rule) => {
      const bad = check("Client Config", rule);
      if (bad) found.push(bad);
    });
    Object.entries(configurationByPackage).forEach(([packageName, lines]) =>
      lines.forEach((rule) => {
        const bad = check(packageName, rule);
        if (bad) found.push(bad);
      })
    );
    inheritedConfigurations.forEach((config) =>
      (config.configurations ?? []).forEach((rule) => {
        const bad = check(config.regex, rule);
        if (bad) found.push(bad);
      })
    );

    return found;
  }, [clientConfig, configurationByPackage, inheritedConfigurations]);

  usePageTitle({
    title: "Policy Editor",
    subTitle: versionNumber ? `v${versionNumber} (Draft)` : "Draft",
  });

  const sitesAndSubnetModified = useMemo(() => sitesAndSubnets.some((site) => site.pendingChanges), [sitesAndSubnets]);
  const configurationsModified = useMemo(() => systemConfigurations.some((site) => site.pendingChanges), [systemConfigurations]);
  const adDomainModified = useMemo(() => activeDirectoryDomains.some((site) => site.pendingChanges), [activeDirectoryDomains]);
  const adOusModified = useMemo(() => activeDirectoryOUs.some((site) => site.pendingChanges), [activeDirectoryOUs]);
  const inheritedConfigsModified = useMemo(
    () =>
      inheritedConfigurations.some(
        (cfg) => cfg.pendingChanges || cfg.isDeleted || cfg.configurations?.some((item) => item.pendingChanges)
      ),
    [inheritedConfigurations]
  );
  const inheritedConfigsChangeCount = useMemo(
    () =>
      inheritedConfigurations.reduce((total, cfg) => {
        const isDirty =
          cfg.pendingChanges || cfg.isDeleted || (cfg.configurations?.some((item) => item.pendingChanges) ?? false);
        return total + (isDirty ? 1 : 0);
      }, 0),
    [inheritedConfigurations]
  );

  const libraryChanges = useMemo(() => {
    const currentAdds = pendingReplicateChanges.additions;
    const initialAdds = initialPendingReplicateChanges.additions;
    const currentRems = pendingReplicateChanges.removals;
    const initialRems = initialPendingReplicateChanges.removals;
    return [
      ...currentAdds.filter((a) => !initialAdds.includes(a)).map((name) => `Add to Library: ${name}`),
      ...initialAdds.filter((a) => !currentAdds.includes(a)).map((name) => `No longer adding: ${name}`),
      ...currentRems.filter((r) => !initialRems.includes(r)).map((name) => `Remove from Library: ${name}`),
      ...initialRems.filter((r) => !currentRems.includes(r)).map((name) => `No longer removing: ${name}`),
    ];
  }, [pendingReplicateChanges, initialPendingReplicateChanges]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const replicateChangesAreUserMade = useMemo(() => {
    const sameSet = (a: string[], b: string[]) =>
      a.length === b.length && a.every((x) => b.includes(x));
    return (
      !sameSet(pendingReplicateChanges.additions, initialPendingReplicateChanges.additions) ||
      !sameSet(pendingReplicateChanges.removals, initialPendingReplicateChanges.removals)
    );
  }, [pendingReplicateChanges, initialPendingReplicateChanges]);

  const hasUnsavedChanges = modifiedRows.length > 0 || clientConfigModified || sitesAndSubnetModified ||
    configurationsModified || adDomainModified || adOusModified || replicateChangesAreUserMade || inheritedConfigsModified;


  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setShowCancelWarning(true);
    } else {
      dispatch(policyEditorActions.resetPolicyEditorState());
      if (currentTenant) {
        navigate(`/${currentTenant.siteId}/policyeditor`);
      }
    }
  };

  const handleReviewClick = async () => {
    if (isReviewing || !versionId || !currentTenant) return;

    if (invalidRules.length > 0 || invalidConfigs.length > 0 || invalidConfigRules.length > 0 || packagesMissingCount > 0) {
      setReviewWarningsOpen(true);
      return;
    }

    setIsReviewing(true);
    try {
      setDiffModalOpen(true);

      const mergedRules = processFinalRules(rules);

      const normalizeConfigLine = (line: IniInfo): IniInfo => {
        if (isCommentRow(line.name, line.value, line.isComment)) return line;
        const lowered = line.name?.toLowerCase() ?? "";
        const name =
          lowered === "pre-run command"
            ? "Pre-run Command"
            : lowered === "post-run command"
            ? "Post-run Command"
            : line.name;
        const normalized = normalizeConfigValue(name ?? "", line.value ?? "", line.delimiter ?? "", line.applicability ?? "");
        return { ...line, name, value: normalized ?? line.value };
      };

      // A section whose lines are all commented and that no rule claims is a configuration for a
      // package the policy no longer deploys. It is dropped, and shows as a removal in the diff.
      const subjectNames = new Set(rules.filter((rule) => !rule.isDeleted && rule.name).map((rule) => rule.name.trim()));
      const isOrphanedSection = (sectionName: string, lines: IniInfo[]) =>
        lines.length > 0 && lines.every((line) => line.isComment) && !subjectNames.has(sectionName.trim());

      const packages = Object.entries(configurationByPackage)
        .filter(([packageName, lines]) => !isOrphanedSection(packageName, lines))
        .map(([packageName, lines]) => ({
        name: packageName,
        inheritance: false,
        lines: lines
          .filter((line) => !line.isDeleted)
          .map(normalizeConfigLine)
          .map((line) => ({
            name: line.name,
            value: line.value,
            isComment: line.isComment ?? false,
            delimiter: line.delimiter,
            inlineComment:
              !line.inlineComment || (typeof line.inlineComment === "string" && line.inlineComment.trim() === "")
                ? null
                : line.inlineComment,
            description: line.description,
            applicability: line.applicability,
            type: line.type,
          })),
      }));

      const inheritedPackages = inheritedConfigurations
        .filter((config) => !config.isDeleted)
        .map((config) => ({
        name: config.regex,
        inheritance: true,
        sortKey: [...(config.matchingPackages ?? [])].sort()[0] ?? config.regex,
        lines: config.configurations
          .filter((line) => !line.isDeleted)
          .map(normalizeConfigLine)
          .map((line) => ({
            name: line.name,
            value: line.value,
            isComment: line.isComment ?? false,
            delimiter: line.delimiter,
            inlineComment:
              !line.inlineComment || (typeof line.inlineComment === "string" && line.inlineComment.trim() === "")
                ? null
                : line.inlineComment,
            description: line.description,
            applicability: line.applicability,
            type: line.type,
          })),
      }));

      const packageConfigSections = [
        ...packages.map((p) => ({ ...p, sortKey: p.name })),
        ...inheritedPackages,
      ]
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        .map(({ sortKey, ...rest }) => rest);

      const clientConfigLines = clientConfig
        .filter((line: IniInfo) => !line.isDeleted)
        .map(normalizeConfigLine)
        .map((line) => ({
          name: line.name,
          value: line.value,
          isComment: line.isComment ?? false,
          delimiter: line.delimiter,
          inlineComment:
            !line.inlineComment || (typeof line.inlineComment === "string" && line.inlineComment.trim() === "")
              ? null
              : line.inlineComment,
          description: line.description,
          applicability: line.applicability,
          type: line.type,
        }));

      const sitesAndSubnetsLines = sitesAndSubnets
        .filter((line) => !line.isDeleted)
        .map((line) => ({
          name: line.name,
          value: line.value,
          delimiter: line.delimiter,
          inlineComment: line.inlineComment,
          description: line.description,
          type: "SiteSubnet",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const systemConfigLines = systemConfigurations
        .filter((config) => !config.isDeleted)
        .map((config) => ({
          name: config.name,
          value: config.value,
          delimiter: config.delimiter,
          inlineComment:
            !config.inlineComment || (typeof config.inlineComment === "string" && config.inlineComment.trim() === "")
              ? null
              : config.inlineComment,
          description: config.description,
          applicability: config.applicability,
          type: config.type || "SystemConfig",
          isComment: config.isComment,
        }));

      const activeDirectoryDomainLines = activeDirectoryDomains
        .filter((domain) => !domain.isDeleted)
        .map((domain) => ({
          name: domain.name,
          value: domain.value,
          delimiter: domain.delimiter,
          inlineComment:
            !domain.inlineComment || (typeof domain.inlineComment === "string" && domain.inlineComment.trim() === "")
              ? null
              : domain.inlineComment,
          description: domain.description,
          applicability: domain.applicability,
          isComment: domain.isComment,
        }));

      const activeDirectoryOULines = activeDirectoryOUs
        .filter((ou) => !ou.isDeleted)
        .map((ou) => ({
          name: ou.name,
          value: ou.value,
          delimiter: ou.delimiter,
          inlineComment:
            !ou.inlineComment || (typeof ou.inlineComment === "string" && ou.inlineComment.trim() === "") ? null : ou.inlineComment,
          description: ou.description,
          applicability: ou.applicability,
          isComment: ou.isComment,
        }));

      const previewResponse = await api.policyEditor.previewVersion(
        currentTenant.siteId,
        versionId,
        {
          applicabilityRules: mergedRules,
          packageConfigSections: packageConfigSections,
          clientConfigSection: clientConfigLines,
          sitesAndSubnetsSection: sitesAndSubnetsLines,
          systemConfigurations: systemConfigLines,
          activeDirectoryDomains: activeDirectoryDomainLines,
          activeDirectoryOUs: activeDirectoryOULines,
          pendingReplicateChanges,
        }
      );

      if (!previewResponse?.isSuccess || !previewResponse.value) {
        throw new Error(previewResponse?.failMessage ?? "Failed to generate preview");
      }

      setOriginalIni(previewResponse.value.originalText);
      setNewIni(previewResponse.value.generatedText);
      setGeneratedText(previewResponse.value.generatedText);
      setReplicateOriginal(previewResponse.value.replicateOriginalText ?? "");
      setReplicateGenerated(previewResponse.value.replicateGeneratedText ?? "");
    } catch (error) {
      setDiffModalOpen(false);
    } finally {
      setIsReviewing(false);
    }
  };

  const handleConfirmWarning = () => {
    setShowCancelWarning(false);
    setCurrentTab(0);
    dispatch(policyEditorActions.resetPolicyEditorState());
    if (currentTenant) {
      navigate(`/${currentTenant.siteId}/policyeditor`);
    }
  };

  const handleSaveToDraft = async (comment?: string) => {
    if (!currentTenant || !versionId || !generatedText || isSaving) return;
    setIsSaving(true);
    try {
      const result = await api.policyEditor.saveVersion(currentTenant.siteId, versionId, generatedText, comment, pendingReplicateChanges);
      if (result?.isSuccess) {
        setDiffModalOpen(false);
        dispatch(policyEditorActions.resetPolicyEditorState());
        navigate(`/${currentTenant.siteId}/policyeditor`);
      } else {
        setErrorMessage({
          title: "Save Failed",
          message: result?.failMessage ?? "Failed to save draft",
          visible: true,
        });
        setDiffModalOpen(false);
      }
    } catch {
      setErrorMessage({ title: "Save Failed", message: "Failed to save draft", visible: true });
      setDiffModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!currentTenant || !versionId) return;
    const loadVersionMetadata = async () => {
      try {
        const response = await api.policyEditor.getPolicyVersion(currentTenant.siteId, versionId);
        if (response?.isSuccess && response.value) {
          if (response.value.status !== CompanyPolicyStatus.Draft) {
            navigate(`/${currentTenant.siteId}/policyeditor`);
            return;
          }
          setVersionNumber(response.value.version);
        }
      } catch {
        navigate(`/${currentTenant.siteId}/policyeditor`);
      }
    };
    loadVersionMetadata();
  }, [api.policyEditor, currentTenant, versionId]);

  // useEffect(() => {
  //   const verifyLicenseFile = async () => {
  //     try {
  //       const response = await api.policyEditor.verifyLicenseFile(currentTenant?.shortName ?? "");
  //       if (response?.isSuccess) {
  //         dispatch(policyEditorActions.setLicenseFile(response.value || false));
  //       } else {
  //         dispatch(policyEditorActions.setLicenseFile(false));
  //       }
  //     } catch (error) {
  //       console.log(error);
  //       dispatch(policyEditorActions.setLicenseFile(false));
  //     }
  //   };
  //
  //   verifyLicenseFile();
  // }, [api.policyEditor, currentTenant?.shortName, dispatch, errorMessage]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handleNavigation = (e: PopStateEvent) => {
      if (hasUnsavedChanges && window.location.pathname.toLowerCase() !== "/policyeditorsubject") {
        e.preventDefault();
        setShowCancelWarning(true);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handleNavigation);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handleNavigation);
    };
  }, [hasUnsavedChanges]);

  const modifiedRules = useMemo(() => {
    return rules.map((rule, index) => ({ index, rule })).filter(({ rule }) => modifiedRows.includes(rule.id!));
  }, [rules, modifiedRows]);

  const pendingChangesBreakdown = useMemo(
    () => [
      { label: "Applicability Rules", count: modifiedRules.length, items: modifiedRules.map(({ rule }) => rule.name) },
      { label: "Library (Replicate.ini)", count: libraryChanges.length, items: libraryChanges },
      { label: "Client Config", count: clientConfigModified ? clientConfig.filter((item) => item.pendingChanges).length : 0 },
      { label: "Sites and Subnets", count: sitesAndSubnetModified ? sitesAndSubnets.filter((item) => item.pendingChanges).length : 0 },
      {
        label: "System Configurations",
        count: configurationsModified ? systemConfigurations.filter((item) => item.pendingChanges).length : 0,
      },
      {
        label: "Active Directory Domains",
        count: adDomainModified ? activeDirectoryDomains.filter((item) => item.pendingChanges).length : 0,
      },
      { label: "Active Directory OUs", count: adOusModified ? activeDirectoryOUs.filter((item) => item.pendingChanges).length : 0 },
      { label: "Inherited Configurations", count: inheritedConfigsChangeCount },
    ],
    [
      modifiedRules,
      libraryChanges,
      clientConfigModified,
      clientConfig,
      sitesAndSubnetModified,
      sitesAndSubnets,
      configurationsModified,
      systemConfigurations,
      adDomainModified,
      activeDirectoryDomains,
      adOusModified,
      activeDirectoryOUs,
      inheritedConfigsChangeCount,
    ]
  );

  const pendingChangesCount = useMemo(
    () => pendingChangesBreakdown.reduce((total, category) => total + category.count, 0),
    [pendingChangesBreakdown]
  );

  // if (!licenseFileValid) {
  //   return (
  //     <div className="denied">
  //       <div className="denied__content">
  //         <img alt="logo" src="/icon.png" style={{ float: "left", width: "50px", height: "50px" }} />
  //         <div className="denied__message">
  //           We were unable to retrieve your license file, or it has expired!
  //           <br />
  //           Please contact the Aiden Customer Success team
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <Box display="flex" gap={1} alignItems="center">
          {hasUnsavedChanges && pendingChangesCount > 0 && (
            <Button
              variant="contained"
              onClick={() => setPendingSummaryOpen(true)}
              sx={{
                backgroundColor: "#fbc02d",
                color: "#000",
                textTransform: "none",
                "&:hover": { backgroundColor: "#e3ac27" },
              }}
            >
              <b style={{ marginRight: "3px" }}>{pendingChangesCount}</b>
              Pending {pendingChangesCount === 1 ? "Change" : "Changes"}
            </Button>
          )}
          <Button variant="outlined" style={{ textTransform: "none" }} onClick={handleCancelClick}>
            Version History
          </Button>
          <Button
            variant="contained"
            style={{ textTransform: "none" }}
            onClick={handleReviewClick}
            disabled={!hasUnsavedChanges || readOnlyViewer || isReviewing}
          >
            {isReviewing ? "Reviewing..." : "Review"}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{
          borderBottom: "1px solid #333",
          "& .MuiTab-root": { color: "#fff", textTransform: "none", fontWeight: "bold" },
        }}
      >
        <Tab disabled={rules.length === 0} label="Applicability Rules" tabIndex={-1} />
        <Tab disabled={rules.length === 0} label="Client Config" tabIndex={-1} />
        <Tab disabled={rules.length === 0} label="Sites and Subnets" tabIndex={-1} />
        <Tab disabled={rules.length === 0} label="System Configurations" tabIndex={-1} />
        <Tab disabled={rules.length === 0} label="Active Directory Domains" tabIndex={-1} />
        <Tab disabled={rules.length === 0} label="Active Directory OUs" tabIndex={-1} />
      </Tabs>

      <Box mt={3}>
        {currentTab === 0 ? (
          <MainApplicabilityRulesTab modifiedRows={modifiedRows} rules={rules} invalidRules={invalidRules} versionId={versionId} />
        ) : currentTab === 1 ? (
          <ClientConfigTab />
        ) : currentTab === 2 ? (
          <SitesAndSubnetsTab />
        ) : currentTab === 3 ? (
          <SystemConfigTab />
        ) : currentTab === 4 ? (
          <ActiveDirectoryDomainTab />
        ) : (
          <ActiveDirectoryOUTab />
        )}
      </Box>

      <PendingChangesSummaryModal
        open={pendingSummaryOpen}
        onClose={() => setPendingSummaryOpen(false)}
        total={pendingChangesCount}
        categories={pendingChangesBreakdown}
      />

      <ReviewWarningsDialog
        open={reviewWarningsOpen}
        onClose={() => setReviewWarningsOpen(false)}
        syntaxErrorsCount={invalidRules.length}
        invalidConfigsCount={invalidConfigs.length}
        invalidConfigRules={invalidConfigRules.map(({ source, error }) => ({ source, error }))}
        packagesMissingCount={packagesMissingCount}
      />

      <DiffModal
        open={diffModalOpen}
        onClose={() => {
          setDiffModalOpen(false);
          setNewIni("");
          setOriginalIni("Loading...");
        }}
        onNext={handleSaveToDraft}
        originalText={originalIni}
        newText={newIni}
        pendingChangesCount={pendingChangesCount}
        canDownloadTheFile={true}
        saveLabel={isSaving ? "Saving..." : "Save to Draft"}
        replicateOriginalText={replicateOriginal}
        replicateGeneratedText={replicateGenerated}
        pendingReplicateChanges={pendingReplicateChanges}
      />
      <ConfirmationDialog
        title="Unsaved Changes"
        subText="You have unsaved changes that will be lost. Leave without saving?"
        isVisible={showCancelWarning}
        onConfirm={handleConfirmWarning}
        onCancel={() => setShowCancelWarning(false)}
      />

      <ErrorMessage
        title={errorMessage.title}
        message={errorMessage.message}
        isVisible={errorMessage.visible}
        onConfirm={() => {
          setErrorMessage({ ...errorMessage, visible: false });
        }}
      />
    </ThemeProvider>
  );
};
